import React, { useState, useEffect } from 'react'
import { Camera, ChevronDown } from 'lucide-react'
import { turbineService, type Photo } from '@/services/turbineService'
import { ErrorBanner } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { photoHasEditedVersions } from '@/utils/photoHelpers'
import turbineWingBg from '@/assets/backgrounds/turbine_wing.png'
import { getRoomDisplayName, getPhotoBlade, photoMatchesBlade, isCategoryFolderPhoto } from '../shared'

// TODO(i18n): 360° sidebar metni; strings.ts bu birimin sahibi değil.
const PHOTOS_LOAD_ERROR = 'Fotoğraf listesi yüklenemedi.'

const RoomPhotoSidebar: React.FC<{
  turbineId: number,
  totalRooms: number,
  selectedRoom: number,
  onRoomSelect: (room: number) => void,
  // Blade seçimi artık ViewModel'de tutuluyor (viewer ile paylaşılıyor).
  selectedBlade: string,
  onBladeSelect: (blade: string) => void,
  onPhotoSelect?: (photo: Photo) => void,
  selectedPhoto?: Photo | null,
  isOpen: boolean,
  currentUser?: any
}> = ({ turbineId, totalRooms, selectedRoom, onRoomSelect, selectedBlade, onBladeSelect, onPhotoSelect, selectedPhoto, isOpen, currentUser }) => {
  const { t } = useLanguage()
  const [roomPhotos, setRoomPhotos] = useState<{ [key: number]: Photo[] }>({})
  // Total photo count per blade (A/B/C) across all rooms — legacy photos count as Blade A.
  const [bladeCounts, setBladeCounts] = useState<{ [blade: string]: number }>({ A: 0, B: 0, C: 0 })
  const [, setLoading] = useState(true)
  // 360 foto listesi yüklenemediğinde eskiden yalnızca console.error vardı:
  // sidebar boş görünüyor, kullanıcı "fotoğraf yok" sanıyordu. Artık ayrı bir
  // hata state'i tutulur, mevcut liste KORUNUR ve "Tekrar dene" sunulur.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Independent dropdown states
  const [isBladeDropdownOpen, setIsBladeDropdownOpen] = useState(false)
  const [isRoomDropdownOpen, setIsRoomDropdownOpen] = useState(false)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.blade-dropdown') && !target.closest('.room-dropdown')) {
        setIsBladeDropdownOpen(false)
        setIsRoomDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Load photos for all rooms from Wasabi
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        setLoading(true)
        // Use the cached list when available; uploads/edits invalidate it explicitly.
        // Forcing noCache here regenerated presigned URLs for every photo on each
        // blade/room change, which defeated all browser/back-end caching.
        const response = await turbineService.getPhotos(turbineId)
        if (response.success && response.data) {
          setLoadError(null)
          // Count photos per blade across the full list (independent of selectedBlade),
          // applying the same room/category filters as the blade filter below.
          const counts: { [blade: string]: number } = { A: 0, B: 0, C: 0 }
          response.data.forEach((photo: any) => {
            const hasRoom = photo.room && photo.room !== null && photo.room !== undefined
            if (!hasRoom || isCategoryFolderPhoto(photo)) return
            // Legacy photos (no blade info anywhere) are attributed to Blade A.
            const blade = getPhotoBlade(photo) || 'A'
            if (counts[blade] !== undefined) counts[blade] += 1
          })
          setBladeCounts(counts)

          const bladePhotos = response.data.filter((photo: any) => {
            const hasRoom = photo.room && photo.room !== null && photo.room !== undefined
            return hasRoom && !isCategoryFolderPhoto(photo) && photoMatchesBlade(photo, selectedBlade)
          })

          const photosByRoom: { [key: number]: Photo[] } = {}

          // Initialize all rooms with empty arrays
          for (let i = 1; i <= totalRooms; i++) {
            photosByRoom[i] = []
          }

          // Populate with actual blade photos only
          bladePhotos.forEach(photo => {
            const room = parseInt(photo.room || '1')
            if (photosByRoom[room]) {
              photosByRoom[room].push(photo)
            }
          })

          // Sort photos by originalName numerically (1, 2, 3...)
          bladePhotos.sort((a, b) => {
            const aName = a.originalName || '';
            const bName = b.originalName || '';
            return aName.localeCompare(bName, undefined, { numeric: true });
          });

          // Re-populate rooms with sorted photos
          Object.keys(photosByRoom).forEach(roomKey => {
            const roomNum = parseInt(roomKey);
            photosByRoom[roomNum] = bladePhotos.filter(photo => {
              const room = parseInt(photo.room || '1');
              return room === roomNum;
            }).sort((a, b) => {
              const aName = a.originalName || '';
              const bName = b.originalName || '';
              return aName.localeCompare(bName, undefined, { numeric: true });
            });
          });

          setRoomPhotos(photosByRoom)
        } else {
          // success:false BİR HATADIR; eskiden else'siz geçilip liste sessizce
          // eski/boş hâlinde kalıyordu.
          console.error('❌ 360° photo list request failed:', response.message)
          setLoadError(responseErrorMessage(response, t, PHOTOS_LOAD_ERROR))
        }
      } catch (error) {
        logError('Turbine360.sidebar.loadPhotos', error)
        // Liste sıfırlanmıyor: hata, "fotoğraf yok" gibi görünmemeli.
        setLoadError(toUserMessage(error, t, PHOTOS_LOAD_ERROR))
      } finally {
        setLoading(false)
      }
        }

    loadPhotos()
  }, [turbineId, totalRooms, selectedBlade, reloadKey])

  const selectedRoomPhotos = roomPhotos[selectedRoom] || []

  // Only rooms that actually contain photos (for the selected blade) are offered
  // in the dropdown. When no room has any photo, fall back to listing every room
  // so the user still sees the "Bu odada fotoğraf bulunamadı" state.
  const roomsWithPhotos = Array.from({ length: totalRooms }, (_, index) => index + 1)
    .filter(room => (roomPhotos[room] || []).length > 0)
  const displayedRooms = roomsWithPhotos.length > 0
    ? roomsWithPhotos
    : Array.from({ length: totalRooms }, (_, index) => index + 1)

  // If the currently selected room has no photos for this blade, jump to the
  // first room that does — so switching blade lands on a room with content.
  const roomsWithPhotosSignature = roomsWithPhotos.join(',')
  useEffect(() => {
    if (roomsWithPhotos.length > 0 && !roomsWithPhotos.includes(selectedRoom)) {
      const firstRoom = roomsWithPhotos[0]
      if (firstRoom !== undefined) onRoomSelect(firstRoom)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsWithPhotosSignature, selectedRoom])

  // Reset photo selection when blade or room changes
  useEffect(() => {
    if (onPhotoSelect && selectedRoomPhotos.length > 0) {
      const firstPhoto = selectedRoomPhotos[0]
      if (firstPhoto) onPhotoSelect(firstPhoto) // Select first photo of the room
    }
  }, [selectedBlade, selectedRoom, selectedRoomPhotos.length])

  // Kullanici bilgisi yoksa panel ONCEDEN sonsuza kadar iskelet gosteriyordu:
  // authService.getCurrentUser() bozuk/eksik oturumda kalici olarak null doner,
  // yani iskelet HIC kaybolmaz ve kullanici sonsuza dek "yukleniyor" saniyordu.
  // Artik ne oldugu ve ne yapmasi gerektigi soyleniyor.
  if (!currentUser) {
    return (
      <div className="w-96 bg-white rounded-xl shadow-sm border p-4">
        <ErrorBanner
          tone="warning"
          message={t.errors.sessionExpired}
        />
      </div>
    )
  }

  if (!isOpen) {
    return null; // Don't render sidebar when closed
  }

  return (
    <div className="w-full h-full bg-white flex flex-col transition-all duration-300 ease-in-out overflow-hidden">
      {/* Sidebar Content */}
      <div className="flex-1 flex flex-col p-4 pt-0 overflow-hidden min-h-0">

      {/* Liste yüklenemedi: hata baglam icinde, listenin tam ustunde. */}
      {loadError && (
        <ErrorBanner
          message={loadError}
          className="mt-4"
          onRetry={() => setReloadKey(prev => prev + 1)}
          onDismiss={() => setLoadError(null)}
        />
      )}

      {/* Custom Blade and Room Selection Dropdowns - Always show but different styling for customer */}
      <div className="mb-4 mt-4 space-y-4">
        {/* Custom Blade Selection Dropdown */}
        <div className="blade-dropdown">
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Blade Seçimi:
          </label>
          <div className="relative">
            <button
              onClick={() => {
                setIsBladeDropdownOpen(!isBladeDropdownOpen)
                setIsRoomDropdownOpen(false)
              }}
              className="w-full bg-white border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium text-left h-10 flex items-center justify-between hover:border-gray-400 transition-colors"
            >
              <span className="text-gray-900">Blade {selectedBlade} <span className="text-gray-400 font-normal">({bladeCounts[selectedBlade] || 0})</span></span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isBladeDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isBladeDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999]">
                {['A', 'B', 'C'].map(blade => (
                  <button
                    key={blade}
                    onClick={() => {
                      onBladeSelect(blade)
                      setIsBladeDropdownOpen(false)
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                      selectedBlade === blade ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    Blade {blade} <span className={selectedBlade === blade ? 'text-blue-500' : 'text-gray-400'}>({bladeCounts[blade] || 0})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom Room Selection Dropdown */}
        <div className="room-dropdown">
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Oda Seçimi:
          </label>
          <div className="relative">
            <button
              onClick={() => {
                setIsRoomDropdownOpen(!isRoomDropdownOpen)
                setIsBladeDropdownOpen(false)
              }}
              className="w-full bg-white border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm font-medium text-left h-10 flex items-center justify-between hover:border-gray-400 transition-colors"
            >
              <span className="text-gray-900">{getRoomDisplayName(selectedRoom)} <span className="text-gray-400 font-normal">({(roomPhotos[selectedRoom] || []).length})</span></span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isRoomDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isRoomDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-48 overflow-y-auto">
                {displayedRooms.map((roomNumber) => {
                  return (
                    <button
                      key={roomNumber}
                      onClick={() => {
                        onRoomSelect(roomNumber)
                        setIsRoomDropdownOpen(false)
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-green-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                        selectedRoom === roomNumber ? 'bg-green-100 text-green-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {getRoomDisplayName(roomNumber)} <span className={selectedRoom === roomNumber ? 'text-green-500' : 'text-gray-400'}>({(roomPhotos[roomNumber] || []).length})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vertical Photo Scroll Area */}
      <div className="border-t pt-4 relative flex-1 min-h-0 overflow-hidden">
        {/* Wing Background - positioned after the border line, wider and longer */}
        {selectedRoomPhotos.length > 0 ? (
          <div
            className="absolute inset-x-0 top-0 bottom-0 bg-cover bg-center opacity-20 transform scale-125"
            style={{
              backgroundImage: `url(${turbineWingBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 30%',
              backgroundRepeat: 'no-repeat',
              zIndex: 0,
              top: '20px', // Start after the border line
              width: '120%', // Wider
              left: '-10%', // Center the wider background
              height: '110%' // Longer
            }}
          ></div>
        ) : null}
        <div className="relative h-full overflow-y-scroll space-y-1.5 pr-3 pb-16 photo-scroll" style={{ scrollbarGutter: 'stable', zIndex: 10 }}>
          {selectedRoomPhotos.length > 0 ? (
            selectedRoomPhotos.map((photo, photoIndex) => {
              // Photos with saved edited versions get a red border to flag them.
              const hasVersions = photoHasEditedVersions(photo)
              return (
                <div
                  key={photo.key}
                  className={`flex items-center justify-center p-2.5 rounded-lg cursor-pointer transition-colors last:mb-6 ${
                    selectedPhoto?.key === photo.key ? 'bg-blue-100' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    if (onPhotoSelect) onPhotoSelect(photo)
                  }}
                >
                  <div
                    className={`flex items-center justify-center p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm min-w-[120px] max-w-[200px] ${
                      hasVersions ? 'border-2 border-red-500' : ''
                    }`}
                  >
                    <span className="text-xs font-medium text-gray-700 truncate">
                      {photo.originalName ? photo.originalName.replace(/\.[^/.]+$/, "") : `Fotoğraf ${photoIndex + 1}`}
                    </span>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="text-center py-8 pb-16">
              <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  )
}

export default RoomPhotoSidebar
